import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const messages = {
  ko: {
    title: '🎰 룰렛 돌리기',
    description: '인스타그램을 팔로우하고\n디스플레이 화면에서 당첨 결과를 확인하세요!',
    buttonDefault: '🎁 인스타그램 팔로우하고,\n랜덤으로 상품받기!',
    buttonLoading: '⏳ 처리 중...',
    step2Title: '✨ 참여 완료!',
    step2Body: '디스플레이 화면에서\n당첨 결과를 확인하세요!',
    step2Highlight: '디스플레이 화면',
    thanks: '감사합니다!',
    errorInvalidQr: '잘못된 QR 코드입니다. 다시 스캔해주세요.',
    errorDeviceNotFound: '디바이스를 찾을 수 없습니다.',
    errorNetwork: '네트워크 오류가 발생했습니다.',
    errorInvalidAccess: '잘못된 접근입니다.',
    errorSpinFailed: '룰렛 시작에 실패했습니다.',
  },
  en: {
    title: '🎰 Spin the Roulette',
    description: 'Follow us on Instagram\nand check the display screen for your prize!',
    buttonDefault: '🎁 Follow on Instagram\nand win a random prize!',
    buttonLoading: '⏳ Processing...',
    step2Title: '✨ You\'re in!',
    step2Body: 'Check the display screen\nfor your prize result!',
    step2Highlight: 'display screen',
    thanks: 'Thank you!',
    errorInvalidQr: 'Invalid QR code. Please scan again.',
    errorDeviceNotFound: 'Device not found.',
    errorNetwork: 'A network error occurred.',
    errorInvalidAccess: 'Invalid access.',
    errorSpinFailed: 'Failed to start roulette.',
  },
};

function detectLang() {
  const lang = navigator.language || navigator.languages?.[0] || 'ko';
  return lang.toLowerCase().startsWith('ko') ? 'ko' : 'en';
}

export default function CustomerRoulette() {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('device');

  const [lang] = useState(() =>
    typeof navigator !== 'undefined' ? detectLang() : 'ko'
  );
  const [step, setStep] = useState(1);
  const [instagramHandle, setInstagramHandle] = useState('loading...');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [buttonText, setButtonText] = useState(() => {
    const detected = typeof navigator !== 'undefined' ? detectLang() : 'ko';
    return messages[detected].buttonDefault;
  });

  const t = messages[lang] || messages.ko;

  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

  useEffect(() => {
    loadDevice();
  }, []);

  async function loadDevice() {
    if (!deviceId) {
      setError(t.errorInvalidQr);
      setButtonDisabled(true);
      return;
    }

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/counter_devices?device_id=eq.${deviceId}&select=instagram_handle`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data || data.length === 0) {
        setError(t.errorDeviceNotFound);
        setButtonDisabled(true);
        return;
      }

      setInstagramHandle(data[0].instagram_handle);
    } catch (err) {
      console.error('Error loading device:', err);
      setError(t.errorNetwork);
      setButtonDisabled(true);
    }
  }

  async function handleSpin() {
    if (!deviceId || !instagramHandle) {
      setError(t.errorInvalidAccess);
      return;
    }

    setButtonDisabled(true);
    setButtonText(t.buttonLoading);

    try {
      // Call start-spin to start roulette animation on display
      const response = await fetch(`${SUPABASE_URL}/functions/v1/start-spin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          device_id: deviceId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || t.errorSpinFailed);
        setButtonDisabled(false);
        setButtonText(t.buttonDefault);
        return;
      }

      // Show completion step immediately
      setStep(2);

      // Redirect to Instagram after 1 second
      setTimeout(() => {
        const appLink = `instagram://user?username=${instagramHandle}`;
        const webLink = `https://instagram.com/${instagramHandle}`;

        window.location.href = appLink;

        setTimeout(() => {
          window.location.href = webLink;
        }, 1500);
      }, 1000);
    } catch (err) {
      console.error('Spin error:', err);
      setError(t.errorNetwork);
      setButtonDisabled(false);
      setButtonText(t.buttonDefault);
    }
  }

  return (
    <div className="min-h-screen flex justify-center items-center p-5" style={{
      background: 'linear-gradient(135deg, #000000 0%, #333333 100%)'
    }}>
      <div className="bg-white rounded-3xl p-12 max-w-md w-full text-center" style={{
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)'
      }}>
        {step === 1 && (
          <div>
            <h1 className="text-gray-900 mb-3 text-4xl font-bold">{t.title}</h1>
            <p className="text-gray-600 mb-10 text-base leading-relaxed">
              {t.description.split('\n').map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>

            <div
              className="inline-block text-white px-4 py-2 rounded-2xl font-semibold mb-8 text-lg"
              style={{
                background: 'linear-gradient(135deg, #000000, #333333, #666666)'
              }}
            >
              @{instagramHandle}
            </div>

            <button
              onClick={handleSpin}
              disabled={buttonDisabled}
              className="w-full py-5 border-none rounded-2xl text-2xl font-bold cursor-pointer transition-all duration-300 text-white disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:-translate-y-0.5 active:enabled:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #000000 0%, #444444 100%)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
              }}
            >
              {buttonText.split('\n').map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </button>

            {error && (
              <div className="bg-black text-white p-4 rounded-xl mt-4 text-sm">
                {error}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h1 className="text-gray-900 mb-3 text-4xl font-bold">{t.step2Title}</h1>

            <div className="text-8xl my-8 animate-bounce">🎉</div>

            <p className="text-gray-800 text-xl leading-relaxed mb-6">
              {t.step2Body.split('\n').map((line, i) => (
                <span key={i}>
                  {i === 0 ? <strong>{line}</strong> : line}
                  {i === 0 && <br />}
                </span>
              ))}
            </p>

            <p className="text-gray-600 text-base mt-6">
              {t.thanks}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
